import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, addDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { ensureAppServiceSignIn } from '../appFirebaseClient';

// Main app database configuration (acc-app-e5316)
const mainAppConfig = {
  apiKey: "AIzaSyDrx0Sxs0yoJHUahGB59ojOEulPfNd57_Y",
  authDomain: "acc-app-e5316.firebaseapp.com",
  projectId: "acc-app-e5316",
  storageBucket: "acc-app-e5316.firebasestorage.app",
  messagingSenderId: "447193481869",
  appId: "1:447193481869:web:318f79b8819a86490245a9"
};

// Initialize main app Firebase
const mainApp = initializeApp(mainAppConfig, 'MAIN_APP');
const mainAppDb = getFirestore(mainApp);

const resolveCompanyPlan = (company) =>
  company?.subscription_details?.subscription?.basePlan?.planId ||
  company?.subscription_details?.subscription?.planId ||
  company?.subscription_details?.subscription?.plan ||
  company?.subscription?.basePlan?.planId ||
  company?.subscription?.planId ||
  company?.subscription?.plan ||
  'starter';

// Plan management functions
export const planDatabase = {
  // Create or update a plan
  createOrUpdatePlan: async (planData) => {
    try {
      const planRef = doc(mainAppDb, 'plans', planData.id);
      await setDoc(planRef, {
        ...planData,
        updatedAt: serverTimestamp(),
        createdAt: planData.createdAt || serverTimestamp()
      }, { merge: true });
      
      console.log(`✅ Plan ${planData.id} created/updated successfully`);
      return { success: true, planId: planData.id };
    } catch (error) {
      console.error('❌ Error creating/updating plan:', error);
      throw error;
    }
  },

  // Get all plans
  getAllPlans: async () => {
    try {
      const plansRef = collection(mainAppDb, 'plans');
      const querySnapshot = await getDocs(plansRef);
      
      const plans = [];
      querySnapshot.forEach((doc) => {
        plans.push({ id: doc.id, ...doc.data() });
      });
      
      return plans;
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      throw error;
    }
  },

  // Get active plans
  getActivePlans: async () => {
    try {
      const plansRef = collection(mainAppDb, 'plans');
      const q = query(plansRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      const plans = [];
      querySnapshot.forEach((doc) => {
        plans.push({ id: doc.id, ...doc.data() });
      });
      
      return plans;
    } catch (error) {
      console.error('❌ Error fetching active plans:', error);
      throw error;
    }
  },

  // Delete a plan
  deletePlan: async (planId) => {
    try {
      const planRef = doc(mainAppDb, 'plans', planId);
      await deleteDoc(planRef);
      
      console.log(`✅ Plan ${planId} deleted successfully`);
      return { success: true, planId };
    } catch (error) {
      console.error('❌ Error deleting plan:', error);
      throw error;
    }
  },

  // Update public page configuration
  updatePublicConfig: async (plans) => {
    try {
      const publicConfigRef = doc(mainAppDb, 'publicConfig', 'pricing');
      await setDoc(publicConfigRef, {
        plans: plans,
        lastUpdated: serverTimestamp(),
        version: '1.0'
      });
      
      console.log('✅ Public page configuration updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating public config:', error);
      throw error;
    }
  },

  // Update feature flags
  updateFeatureFlags: async (featureFlags) => {
    try {
      const featureFlagsRef = doc(mainAppDb, 'featureFlags', 'global');
      await setDoc(featureFlagsRef, {
        ...featureFlags,
        lastUpdated: serverTimestamp()
      });
      
      console.log('✅ Feature flags updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating feature flags:', error);
      throw error;
    }
  },

  // Get company usage
  getCompanyUsage: async (companyId) => {
    try {
      const companyRef = doc(mainAppDb, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        return companySnap.data();
      }
      return null;
    } catch (error) {
      console.error('❌ Error fetching company usage:', error);
      throw error;
    }
  },

  // Update company usage
  updateCompanyUsage: async (companyId, usageData) => {
    try {
      const companyRef = doc(mainAppDb, 'companies', companyId);
      await updateDoc(companyRef, {
        usageCounters: usageData,
        lastUpdated: serverTimestamp()
      });
      
      console.log(`✅ Company ${companyId} usage updated successfully`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating company usage:', error);
      throw error;
    }
  },

  // Sync all plans to main app
  syncAllPlans: async (plans) => {
    try {
      console.log('🔄 Starting plan sync to main app...');
      
      // Update each plan
      for (const plan of plans) {
        await planDatabase.createOrUpdatePlan(plan);
      }
      
      // Update public configuration
      await planDatabase.updatePublicConfig(plans);
      
      console.log('✅ All plans synced successfully to main app');
      return { success: true, syncedPlans: plans.length };
    } catch (error) {
      console.error('❌ Error syncing plans:', error);
      throw error;
    }
  }
};

// Company management functions
export const companyDatabase = {
  // Get all companies via API (same method as working Companies section)
  getAllCompanies: async () => {
    try {
      console.log('🔄 Starting getAllCompanies via API...');
      
      // Import backoffice auth for token
      const { backofficeAuth } = await import('../backofficeFirebaseClient');
      
      const token = await backofficeAuth.currentUser?.getIdToken?.();
      if (!token) {
        throw new Error('Admin authentication required');
      }

      console.log('📊 Fetching companies via API...');
      // Use API endpoint instead of direct Firebase access (same as working Companies section)
      const response = await fetch('https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/companies?limit=1000', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const companies = (data?.companies || []).map(company => ({
        id: company.id,
        name: company.company_info?.firmName || company.name || 'Unknown Company',
        plan: resolveCompanyPlan(company),
        status: company.company_info?.status || 'active',
        created_at: company.company_info?.created_at || company.created_at
      }));
      
      console.log(`✅ Fetched ${companies.length} companies via API`);
      return companies;
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      throw error;
    }
  },

  // Get companies by plan
  getCompaniesByPlan: async (planId) => {
    try {
      const companiesRef = collection(mainAppDb, 'companies');
      const q = query(companiesRef, where('currentPlanId', '==', planId));
      const querySnapshot = await getDocs(q);
      
      const companies = [];
      querySnapshot.forEach((doc) => {
        companies.push({ id: doc.id, ...doc.data() });
      });
      
      return companies;
    } catch (error) {
      console.error('❌ Error fetching companies by plan:', error);
      throw error;
    }
  },

  // Update company plan
  updateCompanyPlan: async (companyId, newPlanId) => {
    try {
      const companyRef = doc(mainAppDb, 'companies', companyId);
      await updateDoc(companyRef, {
        currentPlanId: newPlanId,
        planStartDate: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      
      console.log(`✅ Company ${companyId} plan updated to ${newPlanId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating company plan:', error);
      throw error;
    }
  }
};

// Subscription management functions
export const subscriptionDatabase = {
  // Get all subscriptions
  getAllSubscriptions: async () => {
    try {
      const subscriptionsRef = collection(mainAppDb, 'subscriptions');
      const querySnapshot = await getDocs(subscriptionsRef);
      
      const subscriptions = [];
      querySnapshot.forEach((doc) => {
        subscriptions.push({ id: doc.id, ...doc.data() });
      });
      
      return subscriptions;
    } catch (error) {
      console.error('❌ Error fetching subscriptions:', error);
      throw error;
    }
  },

  // Create subscription
  createSubscription: async (subscriptionData) => {
    try {
      const subscriptionsRef = collection(mainAppDb, 'subscriptions');
      const docRef = await addDoc(subscriptionsRef, {
        ...subscriptionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Subscription created with ID: ${docRef.id}`);
      return { success: true, subscriptionId: docRef.id };
    } catch (error) {
      console.error('❌ Error creating subscription:', error);
      throw error;
    }
  },

  // Update subscription
  updateSubscription: async (subscriptionId, subscriptionData) => {
    try {
      const subscriptionRef = doc(mainAppDb, 'subscriptions', subscriptionId);
      await updateDoc(subscriptionRef, {
        ...subscriptionData,
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Subscription ${subscriptionId} updated successfully`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating subscription:', error);
      throw error;
    }
  }
};

// Get company info by ID via API
export const getCompanyInfo = async (companyId, appId) => {
  try {
    console.log('🔄 Fetching company info via API for:', companyId);
    
    // Import backoffice auth for token
    const { backofficeAuth } = await import('../backofficeFirebaseClient');
    
    const token = await backofficeAuth.currentUser?.getIdToken?.();
    if (!token) {
      throw new Error('Admin authentication required');
    }

    // Use API endpoint to get specific company info
    const response = await fetch(`https://us-central1-acctoo-backoffice.cloudfunctions.net/api/v1/companies/${companyId}`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Company not found
      }
      throw new Error(`API Error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    const company = data?.company || data;
    
    if (company) {
      return {
        id: company.id,
        name: company.company_info?.firmName || company.name || 'Unknown Company',
        plan: resolveCompanyPlan(company),
        status: company.company_info?.status || 'active',
        created_at: company.company_info?.created_at || company.created_at,
        ...company
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching company info:', error);
    throw error;
  }
};

// Get company type label
export const getCompanyTypeLabel = (companyType) => {
  const typeLabels = {
    'proprietorship': 'Proprietorship',
    'partnership': 'Partnership',
    'llp': 'LLP',
    'private': 'Private Limited',
    'public': 'Public Limited',
    'huf': 'HUF'
  };
  
  return typeLabels[companyType] || companyType || 'Unknown';
};

export default mainAppDb;
